<script lang="ts" setup>
import { ref, ComponentPublicInstance } from 'vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import {
  findZipCode,
  createPolicy,
} from '../../../../repositories/landing.repository';
import { wrongDepartureDateTime } from '../../../../helpers';
import storePolicy from '@store/policy';
import moment from 'moment';
import { useI18n } from 'vue-i18n';
import CaptchaCloudflare from '../components/CaptchaCloudflare.vue';
import WizardSummary from '../components/WizardSummary.vue';

type CaptchaExpose = { reset: () => void };

const { t } = useI18n();
const emit = defineEmits<{
  (e: 'next', policyId: number): void;
  (e: 'back'): void;
}>();

const store = storePolicy();
const loading = ref<boolean>(false);
const error = ref<string>('');
const tokenCaptcha = ref<string | null>(null);
const captchaRef = ref<ComponentPublicInstance<CaptchaExpose> | null>(null);

// --- Ruta ---
const sourceZip = ref<string>('');
const sourceCity = ref<string>('');
const destinationZip = ref<string>('');
const destinationCity = ref<string>('');
const hasStops = ref<'si' | 'no'>('no');
const stop1 = ref<string>('');
const stop2 = ref<string>('');
const stop3 = ref<string>('');

// --- Fechas ---
const startDate = ref<string | null>(null);
const startTime = ref<string>('');
const endDate = ref<string | null>(null);
const endTime = ref<string>('');

// --- Datos del viaje ---
const documentDescription = ref<string>('');
const documentFile = ref<any>(null);

// --- Vehículo y conductor ---
const truckPlate = ref<string>('');
const closedBox = ref<'si' | 'no'>('si');
const trailerPlate = ref<string>('');
const driverName = ref<string>('');
const driverCellphone = ref<string>('');
const driverEmail = ref<string>('');

// --- Términos (tal como estaban) ---
const authCheck = ref<boolean>(false);
const faqCheck = ref<boolean>(false);
const docsCheck = ref<boolean>(false);
const privateCheck = ref<boolean>(false);

const CP_INVALID = 'CP INVALIDO';

// CP → ciudad (siempre disabled). Escucha el tipeo con guard de longitud
const lookupZip = async (which: 'source' | 'destination') => {
  const zip = which === 'source' ? sourceZip.value : destinationZip.value;
  if (!zip || zip.length < 4 || zip.length > 7) return;

  const location = await findZipCode(zip as any, false);
  const storeData = store.data;

  if (!location.data?.length) {
    if (which === 'source') {
      sourceCity.value = CP_INVALID;
      store.setProp({
        key: 'data',
        value: { ...storeData, sourceCityZipCodeId: null },
      });
    } else {
      destinationCity.value = CP_INVALID;
      store.setProp({
        key: 'data',
        value: { ...storeData, destinationCityZipCodeId: null },
      });
    }
    return;
  }

  if (which === 'source') {
    sourceCity.value = location.data[0].MunicipioNombre;
    store.setProp({
      key: 'data',
      value: { ...storeData, sourceCityZipCodeId: location.data[0].id },
    });
  } else {
    destinationCity.value = location.data[0].MunicipioNombre;
    store.setProp({
      key: 'data',
      value: { ...storeData, destinationCityZipCodeId: location.data[0].id },
    });
  }
};

// Al elegir fecha de inicio se habilita la de fin (min = inicio)
const handleStartDate = () => {
  endDate.value = null;
};

// El backend recibe el documento con busboy (multipart real), no como texto
// base64. formData.append(key, value) del repositorio ya adjunta un File
// como parte binaria si "value" es un File/Blob real -no hace falta
// convertirlo a base64 nosotros-, así que guardamos el File tal cual.
const handleFile = (e: Event) => {
  const input = e.target as HTMLInputElement;
  documentFile.value = input.files?.[0] ?? null;
};

const handleTokenCaptcha = (token: string) => (tokenCaptcha.value = token);
const handleOnExpired = () => (tokenCaptcha.value = null);

const validEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const handleSubmit = async () => {
  error.value = '';

  if (!tokenCaptcha.value) {
    return (error.value = t(
      'landing.terrestre.contratacion_mx.por_favor_verifica_el_recaptcha',
    ));
  }

  const requiredFilled =
    sourceZip.value &&
    sourceCity.value &&
    destinationZip.value &&
    destinationCity.value &&
    startDate.value &&
    startTime.value &&
    endDate.value &&
    endTime.value &&
    documentDescription.value &&
    truckPlate.value &&
    driverName.value &&
    driverCellphone.value &&
    driverEmail.value &&
    (closedBox.value === 'no' || trailerPlate.value);

  if (!requiredFilled) {
    return (error.value = t(
      'landing.terrestre.contratacion_mx.el_formulario_debe_estar_completo',
    ));
  }

  if (sourceCity.value === CP_INVALID || destinationCity.value === CP_INVALID) {
    return (error.value = t(
      'landing.terrestre.contratacion_mx.codigo_postal_no_valido',
    ));
  }

  if (hasStops.value === 'si' && !stop1.value && !stop2.value && !stop3.value) {
    return (error.value = t('landing.wizard.viaje.error_paradas'));
  }

  if (
    driverCellphone.value.length < 8 ||
    driverCellphone.value.length > 12 ||
    !validEmail(driverEmail.value)
  ) {
    // Mensaje específico: antes reusaba "el formulario debe estar completo",
    // que confundía porque los campos SÍ tenían valor (solo el formato/largo
    // era inválido).
    return (error.value = t('landing.wizard.detalle.error_telefono_email'));
  }

  if (!authCheck.value || !faqCheck.value || !docsCheck.value || !privateCheck.value) {
    return (error.value = t('landing.wizard.viaje.error_terminos'));
  }

  if (wrongDepartureDateTime(startDate.value as string, startTime.value, 'mx')) {
    return (error.value = t(
      'landing.terrestre.contratacion_mx.la_hora_de_salida_no_puede_ser_menor',
    ));
  }

  const travelStartDate = new Date(`${startDate.value}T${startTime.value}`);
  const travelEndDate = new Date(`${endDate.value}T${endTime.value}`);
  const fechaISOstart = moment(travelStartDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
  const fechaISOend = moment(travelEndDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
  if (moment(fechaISOend).isBefore(moment(fechaISOstart))) {
    return (error.value = t('landing.wizard.detalle.error_fecha_fin'));
  }

  const client = store.client ?? {};

  try {
    loading.value = true;

    const createPolicyData = {
      tradeName: client.tradeName,
      rfc: client.rfc,
      assetId: store.data.asset.id,
      subAssetId: store.data.subAsset?.id ?? null,
      productId: store.data.product?.id ?? null,
      indemnificationLimit: store.data.indemnificationLimit,
      sourceCityZipCodeId: store.data.sourceCityZipCodeId,
      destinationCityZipCodeId: store.data.destinationCityZipCodeId,
      companyGPSId: client?.companyGPS?.id,
      truckPlate: truckPlate.value,
      trailerPlate: closedBox.value === 'si' ? trailerPlate.value : '',
      driverName: driverName.value,
      documentDescription: documentDescription.value,
      driverCellphone: driverCellphone.value,
      driverEmail: driverEmail.value,
      travelStartDate: fechaISOstart,
      travelEndDate: fechaISOend,
      hasStops: hasStops.value === 'si' ? 'Si' : 'No',
      stop1: stop1.value,
      stop2: stop2.value,
      stop3: stop3.value,
      document: documentFile.value ?? null,
      token: tokenCaptcha.value,
    };

    // El cliente ya fue creado/actualizado en el paso "Revisa tus datos"
    const { data } = await createPolicy(
      client.id,
      client.managerId,
      createPolicyData,
    );

    loading.value = false;
    if (data) {
      // vigencia para el paso "Tu póliza"
      store.setProp({
        key: 'data',
        value: {
          ...store.data,
          travelStartDate: fechaISOstart,
          travelEndDate: fechaISOend,
        },
      });
      emit('next', data.id);
    }
  } catch (e: any) {
    error.value = e.message ?? t('global.ocurrio_un_error');
    loading.value = false;
  } finally {
    captchaRef.value?.reset();
  }
};
</script>

<template>
  <section class="wz-screen">
    <div class="wz-grid">
      <div class="wz-card wz-trip-form">
        <header class="wz-trip-header">
          <h2>{{ t('landing.wizard.viaje.titulo') }}</h2>
          <p class="sub">{{ t('landing.wizard.viaje.sub') }}</p>
        </header>

        <!-- A · Ruta -->
        <fieldset class="wz-trip-group">
          <legend>
            <span class="wz-trip-num">A</span>
            <span class="wz-trip-title">{{ t('landing.wizard.viaje.sec_ruta') }}</span>
          </legend>

          <div class="wz-grid-2">
            <div class="wz-field">
              <label>{{ t('landing.terrestre.contratacion_mx.ciudad_de_origen') }}</label>
              <input
                v-model="sourceZip"
                type="text"
                :placeholder="t('landing.terrestre.contratacion_mx.codigo_postal_placeholder')"
                @input="lookupZip('source')"
              />
            </div>
            <div class="wz-field">
              <label>&nbsp;</label>
              <input v-model="sourceCity" type="text" disabled />
            </div>
          </div>

          <div class="wz-grid-2">
            <div class="wz-field">
              <label>{{ t('landing.terrestre.contratacion_mx.ciudad_de_destino_final') }}</label>
              <input
                v-model="destinationZip"
                type="text"
                :placeholder="t('landing.terrestre.contratacion_mx.codigo_postal_placeholder')"
                @input="lookupZip('destination')"
              />
            </div>
            <div class="wz-field">
              <label>&nbsp;</label>
              <input v-model="destinationCity" type="text" disabled />
            </div>
          </div>

          <div class="wz-field">
            <label>{{ t('landing.wizard.viaje.paradas_pregunta') }}</label>
            <div class="wz-radio-row">
              <label class="wz-radio-chip" :class="{ checked: hasStops === 'no' }">
                <input v-model="hasStops" type="radio" value="no" />
                <span>{{ t('landing.wizard.viaje.paradas_no') }}</span>
              </label>
              <label class="wz-radio-chip" :class="{ checked: hasStops === 'si' }">
                <input v-model="hasStops" type="radio" value="si" />
                <span>{{ t('landing.wizard.viaje.paradas_si') }}</span>
              </label>
            </div>
          </div>

          <div v-if="hasStops === 'si'" class="wz-grid-3">
            <div class="wz-field">
              <label>{{ t('landing.terrestre.contratacion_mx.parada_1') }}</label>
              <input v-model="stop1" type="text" />
            </div>
            <div class="wz-field">
              <label>{{ t('landing.terrestre.contratacion_mx.parada_2') }}</label>
              <input v-model="stop2" type="text" />
            </div>
            <div class="wz-field">
              <label>{{ t('landing.terrestre.contratacion_mx.parada_3') }}</label>
              <input v-model="stop3" type="text" />
            </div>
          </div>
        </fieldset>

        <!-- B · Fechas y horarios -->
        <fieldset class="wz-trip-group">
          <legend>
            <span class="wz-trip-num">B</span>
            <span class="wz-trip-title">{{ t('landing.wizard.viaje.sec_fechas') }}</span>
          </legend>

          <div class="wz-grid-2">
            <div class="wz-field">
              <label>{{ t('landing.terrestre.contratacion_mx.fecha_inicio_viaje') }}</label>
              <VueDatePicker
                v-model="startDate"
                :enable-time-picker="false"
                format="dd/MM/yyyy"
                model-type="yyyy-MM-dd"
                :min-date="new Date()"
                auto-apply
                :placeholder="t('landing.terrestre.contratacion_mx.fecha_inicio_viaje')"
                @update:model-value="handleStartDate"
              />
            </div>
            <div class="wz-field">
              <label>{{ t('landing.terrestre.contratacion_mx.hora_inicio_viaje') }}</label>
              <input v-model="startTime" type="time" />
            </div>
          </div>

          <div class="wz-grid-2">
            <div class="wz-field">
              <label>{{ t('landing.terrestre.contratacion_mx.fecha_fin_viaje') }}</label>
              <VueDatePicker
                v-model="endDate"
                :enable-time-picker="false"
                format="dd/MM/yyyy"
                model-type="yyyy-MM-dd"
                :min-date="startDate ? new Date(`${startDate}T00:00:00`) : new Date()"
                :disabled="!startDate"
                auto-apply
                :placeholder="t('landing.terrestre.contratacion_mx.fecha_fin_viaje')"
              />
            </div>
            <div class="wz-field">
              <label>{{ t('landing.terrestre.contratacion_mx.hora_fin_viaje') }}</label>
              <input v-model="endTime" type="time" />
            </div>
          </div>

          <p class="wz-help">{{ t('landing.wizard.viaje.fechas_hint') }}</p>
        </fieldset>

        <!-- C · Datos del viaje -->
        <fieldset class="wz-trip-group">
          <legend>
            <span class="wz-trip-num">C</span>
            <span class="wz-trip-title">{{ t('landing.wizard.viaje.sec_datos') }}</span>
          </legend>

          <div class="wz-field">
            <label>{{ t('landing.terrestre.contratacion_mx.documentos_del_viaje') }}</label>
            <input
              v-model="documentDescription"
              type="text"
              :placeholder="t('landing.terrestre.contratacion_mx.documentos_del_viaje')"
            />
          </div>
          <div class="wz-field">
            <label>{{ t('landing.terrestre.contratacion_mx.adjuntar_documentos') }}</label>
            <input type="file" class="wz-file" @change="handleFile" />
          </div>
        </fieldset>

        <!-- D · Vehículo y conductor -->
        <fieldset class="wz-trip-group">
          <legend>
            <span class="wz-trip-num">D</span>
            <span class="wz-trip-title">{{ t('landing.wizard.viaje.sec_vehiculo') }}</span>
          </legend>

          <div class="wz-grid-2">
            <div class="wz-field">
              <label>{{ t('landing.terrestre.contratacion_mx.placa_tractor') }}</label>
              <input
                v-model="truckPlate"
                type="text"
                style="text-transform: uppercase"
              />
            </div>
            <div class="wz-field">
              <label>{{ t('landing.wizard.viaje.caja_pregunta') }}</label>
              <div class="wz-radio-row">
                <label class="wz-radio-chip" :class="{ checked: closedBox === 'si' }">
                  <input v-model="closedBox" type="radio" value="si" />
                  <span>{{ t('landing.wizard.viaje.si') }}</span>
                </label>
                <label class="wz-radio-chip" :class="{ checked: closedBox === 'no' }">
                  <input v-model="closedBox" type="radio" value="no" />
                  <span>{{ t('landing.wizard.viaje.no') }}</span>
                </label>
              </div>
            </div>
          </div>

          <div v-if="closedBox === 'si'" class="wz-field">
            <label>{{ t('landing.terrestre.contratacion_mx.placa_caja') }}</label>
            <input
              v-model="trailerPlate"
              type="text"
              style="text-transform: uppercase"
            />
          </div>

          <div class="wz-field">
            <label>{{ t('landing.terrestre.contratacion_mx.nombre_del_conductor') }}</label>
            <input v-model="driverName" type="text" />
          </div>

          <div class="wz-grid-2">
            <div class="wz-field">
              <label>{{ t('landing.terrestre.contratacion_mx.movil_del_conductor') }}</label>
              <input v-model="driverCellphone" type="tel" />
            </div>
            <div class="wz-field">
              <label>{{ t('landing.terrestre.contratacion_mx.email_del_conductor') }}</label>
              <input v-model="driverEmail" type="email" />
            </div>
          </div>
        </fieldset>

        <p class="wz-legal">
          {{ t('landing.terrestre.contratacion_mx.seguro_de_propiedad') }}
        </p>

        <!-- Términos (tal como estaban) -->
        <fieldset class="wz-trip-group wz-trip-group--terms">
          <label class="wz-terms-check">
            <input v-model="authCheck" type="checkbox" />
            <span>{{ t('landing.terrestre.contratacion_mx.termino1') }}</span>
          </label>
          <label class="wz-terms-check">
            <input v-model="faqCheck" type="checkbox" />
            <span>
              {{ t('landing.terrestre.contratacion_mx.acepto_los') }}
              <a
                target="_blank"
                href="https://www.chubb.com/content/dam/chubb-sites/chubb-com/mx-es/condiciones-generales/global/documents/pdf/ad33-aba-d-transporte-de-carga-especifica.pdf"
              >{{ t('landing.terrestre.contratacion_mx.terminos_y_condiciones') }}</a>
            </span>
          </label>
          <label class="wz-terms-check">
            <input v-model="docsCheck" type="checkbox" />
            <span>{{ t('landing.terrestre.contratacion_mx.termino2') }}</span>
          </label>
          <label class="wz-terms-check">
            <input v-model="privateCheck" type="checkbox" />
            <span>
              {{ t('landing.terrestre.contratacion_mx.termino3') }}
              <a
                target="_blank"
                href="https://www.chubb.com/content/dam/chubb-sites/chubb-com/mx-es/footer/privacy-notices/documents/pdf/aviso-de-privacidad-asegurados-chubb-seguros-mexico.pdf"
              >{{ t('landing.terrestre.contratacion_mx.aqui') }}</a>
            </span>
          </label>
        </fieldset>

        <p v-if="error" class="wz-error">{{ error }}</p>

        <CaptchaCloudflare
          ref="captchaRef"
          @verified="handleTokenCaptcha"
          @expired="handleOnExpired"
        />

        <button
          type="button"
          class="wz-cta blue"
          :disabled="loading"
          @click="handleSubmit"
        >
          <span v-if="loading">{{ t('landing.wizard.detalle.emitiendo') }}</span>
          <span v-else>{{ t('landing.terrestre.contratacion_mx.contratar_el_seguro') }} →</span>
        </button>
        <button type="button" class="wz-back" @click="emit('back')">
          {{ t('landing.wizard.detalle.volver') }}
        </button>
      </div>

      <WizardSummary />
    </div>
  </section>
</template>
